import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  FormGroup,
  FormFeedback,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";

import { provincesRepo } from "data/repositories/provincesRepo";
import { adminThemes, provinceIds } from "data/admin/domain";
import { adminPaths } from "admin/paths";
import { normalizeSlug, seasonOptions } from "admin/contentUtils";
import AdminFormActions from "components/admin/AdminFormActions";
import AdminPageHeader from "components/admin/AdminPageHeader";
import RepeaterField from "components/admin/RepeaterField";

function emptyProvince() {
  return {
    id: "",
    name: "",
    slogan: "",
    story: "",
    heroImage: "",
    foods: "",
    themes: [],
    highlights: [],
    experiences: [],
    seasons: [],
  };
}

function mapProvinceToForm(current) {
  return {
    id: current.id || "",
    name: current.name || "",
    slogan: current.slogan || "",
    story: current.story || "",
    heroImage: current.heroImage || current.image || "",
    foods: Array.isArray(current.foods) ? current.foods.join(", ") : "",
    themes: current.themes || [],
    highlights: Array.isArray(current.highlights) ? current.highlights : [],
    experiences: Array.isArray(current.experiences) ? current.experiences : [],
    seasons: current.seasons || [],
  };
}

function validateForm(form, isNew, provinceId) {
  const nextErrors = {};
  const normalizedId = normalizeSlug(form.id);

  if (!normalizedId) nextErrors.id = "Province ID zorunludur.";
  else if (!provinceIds.includes(normalizedId)) nextErrors.id = "ID mobile tarafındaki provinceIds listesinde bulunmalı.";
  else if (isNew && provincesRepo.getById(normalizedId)) nextErrors.id = "Bu ID ile kayıt zaten var.";
  else if (!isNew && normalizedId !== provinceId) nextErrors.id = "Var olan kayıt için ID değiştirilemez.";

  if (!form.name.trim()) nextErrors.name = "İl adı zorunludur.";
  if (!form.slogan.trim()) nextErrors.slogan = "Slogan zorunludur.";
  if (!form.story.trim()) nextErrors.story = "Hikaye alanı zorunludur.";
  if (!form.themes.length) nextErrors.themes = "En az bir tema seçin.";
  if (!form.seasons.length) nextErrors.seasons = "En az bir sezon seçin.";

  return { nextErrors, normalizedId };
}

export default function ProvinceEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const provinceId = params.id;
  const isNew = provinceId === "new" || !provinceId;

  const [form, setForm] = useState(emptyProvince());
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const readOnlyId = !isNew;

  const title = isNew ? "Yeni il" : `İl düzenle: ${provinceId}`;

  useEffect(() => {
    setError("");
    if (isNew) {
      const next = emptyProvince();
      setForm(next);
      setErrors({});
      return;
    }

    const current = provincesRepo.getById(provinceId);
    if (!current) {
      setError("İl bulunamadı");
      return;
    }
    setForm(mapProvinceToForm(current));
    setErrors({});
  }, [isNew, provinceId]);

  const selectedThemes = useMemo(() => new Set(form.themes || []), [form.themes]);

  const toggleTheme = (t) => {
    const next = new Set(selectedThemes);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    setForm((prev) => ({ ...prev, themes: Array.from(next) }));
  };

  const onSave = (e) => {
    e.preventDefault();
    setError("");
    const { nextErrors, normalizedId } = validateForm(form, isNew, provinceId);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return;
    }

    const payload = {
      id: normalizedId,
      name: form.name.trim(),
      slogan: form.slogan.trim(),
      story: form.story.trim(),
      heroImage: form.heroImage.trim(),
      foods: String(form.foods || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      themes: form.themes,
      highlights: form.highlights.filter((item) => item?.trim()),
      experiences: form.experiences.filter((item) => item?.trim()),
      seasons: form.seasons,
    };

    try {
      setSaving(true);
      if (isNew) {
        provincesRepo.create(payload);
      } else {
        provincesRepo.update(form.id, payload);
      }

      navigate(`/admin${adminPaths.content.provinces.list}`, {
        state: {
          status: {
            color: "success",
            message: `${payload.name} başarıyla ${isNew ? "oluşturuldu" : "güncellendi"}.`,
          },
        },
      });
    } catch (err) {
      setError(err?.message || "Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminPageHeader
        gradient="info"
        title={title}
        description="Tema, sezon ve içerik alanları mobile modeline göre tutulur."
        backAction={() => navigate(`/admin${adminPaths.content.provinces.list}`)}
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col xl="8">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Temel bilgiler</h3>
              </CardHeader>
              <CardBody>
                {error ? <Alert color="danger">{error}</Alert> : null}

                <Form onSubmit={onSave}>
                  <FormGroup>
                    <Label>ID (slug)</Label>
                    <Input
                      value={form.id}
                      onChange={(e) => setForm((p) => ({ ...p, id: normalizeSlug(e.target.value) }))}
                      placeholder="ör: trabzon"
                      disabled={readOnlyId}
                      invalid={Boolean(errors.id)}
                    />
                    <FormFeedback>{errors.id}</FormFeedback>
                    <div className="small text-muted mt-2">Geçerli ID örnekleri: {provinceIds.slice(0, 6).join(", ")}...</div>
                  </FormGroup>

                  <FormGroup>
                    <Label>Ad</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Trabzon"
                      invalid={Boolean(errors.name)}
                    />
                    <FormFeedback>{errors.name}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Slogan</Label>
                    <Input
                      value={form.slogan}
                      onChange={(e) => setForm((p) => ({ ...p, slogan: e.target.value }))}
                      placeholder="Kısa slogan"
                      invalid={Boolean(errors.slogan)}
                    />
                    <FormFeedback>{errors.slogan}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Hikaye</Label>
                    <Input
                      type="textarea"
                      rows="6"
                      value={form.story}
                      onChange={(e) => setForm((p) => ({ ...p, story: e.target.value }))}
                      placeholder="Storytelling metni"
                      invalid={Boolean(errors.story)}
                    />
                    <FormFeedback>{errors.story}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Kapak görseli URL</Label>
                    <Input
                      value={form.heroImage}
                      onChange={(e) => setForm((p) => ({ ...p, heroImage: e.target.value }))}
                      placeholder="https://..."
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Yerel lezzetler</Label>
                    <Input
                      value={form.foods}
                      onChange={(e) => setForm((p) => ({ ...p, foods: e.target.value }))}
                      placeholder="Kuymak, Akçaabat köftesi"
                    />
                  </FormGroup>

                  <div className="mb-3">
                    <Label className="d-block">Temalar</Label>
                    <Row>
                      {adminThemes.map((t) => (
                        <Col md="4" key={t} className="mb-2">
                          <div className="custom-control custom-checkbox">
                            <input
                              className="custom-control-input"
                              id={`theme-${t}`}
                              type="checkbox"
                              checked={selectedThemes.has(t)}
                              onChange={() => toggleTheme(t)}
                            />
                            <label className="custom-control-label" htmlFor={`theme-${t}`}>
                              {t}
                            </label>
                          </div>
                        </Col>
                      ))}
                    </Row>
                    {errors.themes ? <div className="small text-danger">{errors.themes}</div> : null}
                  </div>

                  <div className="mb-4">
                    <Label className="d-block">Sezonlar</Label>
                    {seasonOptions.map((season) => (
                      <Button
                        key={season}
                        type="button"
                        color={form.seasons.includes(season) ? "info" : "secondary"}
                        outline
                        className="mr-2 mb-2"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            seasons: prev.seasons.includes(season)
                              ? prev.seasons.filter((item) => item !== season)
                              : [...prev.seasons, season],
                          }))
                        }
                      >
                        {season}
                      </Button>
                    ))}
                    {errors.seasons ? <div className="small text-danger">{errors.seasons}</div> : null}
                  </div>

                  {isNew ? null : (
                    <div className="mb-3">
                      <Label className="d-block">Durum</Label>
                      <Badge color={provincesRepo.getById(provinceId)?.deletedAt ? "warning" : "success"} pill>
                        {provincesRepo.getById(provinceId)?.deletedAt ? "Arşivde" : "Aktif"}
                      </Badge>
                    </div>
                  )}

                  <AdminFormActions saving={saving} onCancel={() => navigate(`/admin${adminPaths.content.provinces.list}`)} />
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">İçerik blokları</h3>
              </CardHeader>
              <CardBody>
                <RepeaterField
                  label="Highlights"
                  description="Kart ve keşif akışında gösterilen kısa içerik maddeleri."
                  values={form.highlights}
                  onChange={(values) => setForm((prev) => ({ ...prev, highlights: values }))}
                  placeholder="Sümela Manastırı"
                />

                <RepeaterField
                  label="Experiences"
                  description="İl deneyimlerinde sunulan aktiviteleri metin olarak yönetin."
                  values={form.experiences}
                  onChange={(values) => setForm((prev) => ({ ...prev, experiences: values }))}
                  placeholder="Yayla rotalarında yürüyüş"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
